import { useCallback } from 'react';
import type { Command } from '../types';

const COMMANDS: Command[] = [
  {
    name: 'clear',
    description: 'Clear the conversation',
    pattern: /^\/clear$/i,
    execute: () => 'CLEAR_CONVERSATION',
  },
  {
    name: 'help',
    description: 'Show available commands',
    pattern: /^\/help$/i,
    execute: () => {
      return `Available commands:
- /clear - Clear the conversation
- /help - Show this help message
- /model [name] - Switch to a different model
- /summarize - Summarize the conversation
- /export - Export conversation to markdown`;
    },
  },
  {
    name: 'model',
    description: 'Switch model',
    pattern: /^\/model\s+(.+)$/i,
    execute: (args) => {
      return `MODEL_SWITCH:${args[0]}`;
    },
  },
  {
    name: 'summarize',
    description: 'Summarize the conversation',
    pattern: /^\/summarize$/i,
    execute: () => {
      return 'Please provide a concise summary of our conversation so far, highlighting key points and decisions.';
    },
  },
  {
    name: 'export',
    description: 'Export conversation',
    pattern: /^\/export$/i,
    execute: () => 'EXPORT_CONVERSATION',
  },
];

export function useCommands() {
  const parseCommand = useCallback(
    (input: string): { isCommand: boolean; result: string | null } => {
      const trimmedInput = input.trim();

      for (const command of COMMANDS) {
        const match = trimmedInput.match(command.pattern);
        if (match) {
          const args = match.slice(1);
          const result = command.execute(args);
          return { isCommand: true, result };
        }
      }

      return { isCommand: false, result: null };
    },
    []
  );

  const getCommandSuggestions = useCallback((input: string): Command[] => {
    if (!input.startsWith('/')) return [];

    const query = input.toLowerCase();
    return COMMANDS.filter(
      (cmd) =>
        `/${cmd.name}`.startsWith(query) ||
        cmd.description.toLowerCase().includes(query.slice(1))
    );
  }, []);

  return {
    parseCommand,
    getCommandSuggestions,
    availableCommands: COMMANDS,
  };
}
